import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CanvaPicker, { canvaSourceFromUrl } from "./CanvaPicker";

/* ─── Score types ─────────────────────────────────────────────────
   Partagé entre ConfigEditor (dictionnaire de scores) et DecisionEditor
   (impacts par score), pour garder les mêmes libellés/couleurs partout.
*/
export const SCORE_TYPES = ["metric", "actor", "cbs-submetric", "custom"];

export const SCORE_TYPE_LABELS = {
  metric: "Métriques",
  actor: "Acteurs",
  "cbs-submetric": "CBS (sous-métriques)",
  custom: "Scores personnalisés",
};

export const SCORE_TYPE_DESC = {
  metric: "Indicateur numérique simple (eau, argent, biodiversité…)",
  actor: "Satisfaction d'un acteur. Affiche son rôle, son icône et sa description.",
  "cbs-submetric": "Composant du Coefficient de Biotope par Surface. Utilise un coefficient (0.0–1.0).",
  custom: "Score calculé dynamiquement (ex: sum_actor, vote).",
};

/* ─── Slug validation ─────────────────────────────────────────────── */
const SLUG_RE = /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/;

export function isValidSlug(id) {
  return SLUG_RE.test(id || "");
}

export const SLUG_HINT = "Lettres minuscules, chiffres, tirets ou underscores uniquement (ex: atelier_btp_v2). Pas d'espace ni d'accent.";

/* ─── TAG_SUGGESTIONS ───────────────────────────────────────────────
   Suggestions de tags partagées entre ConfigEditor, PageComposer et
   DecisionEditor. Un tag identifie à la fois une "famille" d'atelier
   (utilisée pour filtrer les configs) et une clé de variation possible
   dans les conséquences de décision (variation_game_config).
*/
export const TAG_SUGGESTIONS = ["btp", "acg", "disney", "ville_paris", "tp", "new"];

/* ─── MediaUpload / ImageUpload ─────────────────────────────────────
   Drag-and-drop + click-to-select uploader.
   Uploads to /upload_image (ou /upload_media) et renvoie l'URL Cloudinary via
   onChange. On peut aussi coller une URL directement.

   TROISIÈME SOURCE : CANVA. Une diapo préparée dans Canva se choisit ici
   directement, sans passer par l'export manuel puis l'upload. Le bouton est
   posé sur ImageUpload plutôt que sur un éditeur en particulier parce que
   toutes les images du builder — décisions, pages, étapes — peuvent venir de
   là ; les onze points d'appel en héritent sans rien changer.

   Une image déjà venue de Canva se reconnaît à son URL (cf.
   `canvaSourceFromUrl`), ce qui permet de proposer « Mettre à jour » au lieu
   de « Importer », sans stocker le moindre champ supplémentaire dans les
   documents du jeu.

   POURQUOI IL ACCEPTE AUTRE CHOSE QUE DES IMAGES. Une page de contexte peut
   porter une vidéo ou une bande son, et jusqu'ici l'auteur devait déposer son
   fichier sur Cloudinary par un autre chemin puis en recopier l'URL à la main —
   pour un son, c'était le SEUL chemin. La route `/upload_media` sait déjà
   recevoir n'importe quel média (`resource_type="auto"`), il ne manquait que
   la zone de dépôt. Tout le reste — glisser-déposer, saisie d'URL, suppression
   — est identique, d'où un seul composant et non trois.
*/
const UPLOAD_KINDS = {
  image: {
    mime: "image/",
    inputAccept: "image/*",
    endpoint: "/upload_image",
    field: "image",
    responseKey: "image_url",
    icon: "🖼",
    wrong: "Veuillez sélectionner un fichier image.",
    hint: "PNG, JPG, WEBP — Cloudinary",
    replace: "Changer l'image",
  },
  media: {
    // Une page de contexte accepte l'un OU l'autre, et le format du média se
    // déduit ensuite de l'URL : il n'y a donc pas de question à poser ici.
    mime: ["image/", "video/"],
    inputAccept: "image/*,video/*",
    endpoint: "/upload_media",
    field: "media",
    responseKey: "media_url",
    icon: "🎞",
    wrong: "Veuillez sélectionner une image ou une vidéo.",
    hint: "Image ou vidéo — Cloudinary",
    replace: "Changer le média",
  },
  audio: {
    mime: "audio/",
    inputAccept: "audio/*",
    endpoint: "/upload_media",
    field: "media",
    responseKey: "media_url",
    icon: "🔊",
    wrong: "Veuillez sélectionner un fichier audio.",
    hint: "MP3, WAV, M4A — Cloudinary",
    replace: "Changer le son",
  },
};

const VIDEO_URL = /(\.(mp4|webm|ogv|mov|m4v)(\?|#|$)|youtube\.com|youtu\.be|vimeo\.com|\/video\/upload\/)/i;
const AUDIO_URL = /\.(mp3|wav|m4a|aac|oga|flac)(\?|#|$)/i;

export function MediaUpload({ value, onChange, token, label = "Média", accept = "image" }) {
  const kind = UPLOAD_KINDS[accept] || UPLOAD_KINDS.image;
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [canvaOpen, setCanvaOpen] = useState(false);
  const fileRef = useRef();

  // Canva ne produit que des images : le proposer pour un son n'aurait mené
  // qu'à une boîte de dialogue sans réponse utile.
  const canvaSource = accept === "audio" ? null : canvaSourceFromUrl(value);
  const showCanva = accept !== "audio";

  const accepts = (type) =>
    (Array.isArray(kind.mime) ? kind.mime : [kind.mime]).some((prefix) => type.startsWith(prefix));

  const upload = async (file) => {
    if (!file || !accepts(file.type)) {
      alert(kind.wrong);
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append(kind.field, file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${kind.endpoint}`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: form,
      });
      const data = await res.json();
      if (data[kind.responseKey]) onChange(data[kind.responseKey]);
      else alert("Erreur lors de l'upload.");
    } catch {
      alert("Erreur réseau lors de l'upload.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  return (
    <div className="image-upload-wrapper">
      {label && <div className="builder-label-text" style={{ marginBottom: 6 }}>{label}</div>}

      {/* Drop zone */}
      <div
        className={`image-upload-zone ${dragging ? "dragging" : ""} ${uploading ? "uploading" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileRef.current.click()}
      >
        {value ? (
          <>
            <MediaPreview url={value} />
            <div className="image-upload-overlay">
              <span>{uploading ? "Upload en cours…" : kind.replace}</span>
            </div>
          </>
        ) : (
          <div className="image-upload-placeholder">
            {uploading ? (
              <span>Upload en cours…</span>
            ) : (
              <>
                <span className="image-upload-icon">{kind.icon}</span>
                <span>Glisser-déposer ou <u>cliquer</u> pour choisir</span>
                <span className="image-upload-hint">{kind.hint}</span>
              </>
            )}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={kind.inputAccept}
          style={{ display: "none" }}
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
        />
      </div>

      {/* URL fallback toggle */}
      <div className="image-upload-url-row">
        {showCanva && (
          <button
            className="image-upload-url-toggle canva"
            onClick={() => setCanvaOpen(true)}
            type="button"
          >
            {canvaSource ? "↻ Mettre à jour depuis Canva" : "◆ Importer depuis Canva"}
          </button>
        )}
        <button
          className="image-upload-url-toggle"
          onClick={() => setShowUrl((v) => !v)}
          type="button"
        >
          {showUrl ? "▲ Masquer l'URL" : "🔗 Saisir une URL manuellement"}
        </button>
        {value && (
          <button
            className="image-upload-url-toggle danger"
            onClick={() => onChange("")}
            type="button"
          >
            ✕ Supprimer
          </button>
        )}
      </div>
      {showUrl && (
        <input
          className="builder-input"
          style={{ marginTop: 4 }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://res.cloudinary.com/..."
        />
      )}

      {canvaOpen && (
        <CanvaPicker
          token={token}
          source={canvaSource}
          currentUrl={canvaSource ? value : null}
          onPublished={onChange}
          onClose={() => setCanvaOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * L'aperçu de ce qui est déposé, DÉDUIT DE L'URL et non du champ qui l'accueille.
 * Une même zone reçoit indifféremment une image ou une vidéo ; et une URL collée
 * à la main peut désigner autre chose que ce que la zone annonçait. C'est donc
 * l'adresse qui décide, comme à l'affichage dans le jeu.
 */
function MediaPreview({ url }) {
  if (AUDIO_URL.test(url)) {
    return (
      <div className="image-upload-audio">
        <span className="image-upload-icon">🔊</span>
        {/* Écouter avant de projeter : c'est la seule façon de vérifier qu'on a
            déposé la bonne prise, et le lecteur tient dans la vignette. */}
        <audio src={url} controls onClick={(e) => e.stopPropagation()} />
      </div>
    );
  }
  if (VIDEO_URL.test(url)) {
    // Une vidéo hébergée (YouTube, Vimeo) ne se lit pas dans une balise
    // <video> : on la nomme plutôt que d'afficher un cadre noir.
    if (/(youtube\.com|youtu\.be|vimeo\.com)/i.test(url)) {
      return (
        <div className="image-upload-audio">
          <span className="image-upload-icon">▶</span>
          <span className="image-upload-hint">Vidéo hébergée</span>
        </div>
      );
    }
    return <video src={url} className="image-upload-preview" muted />;
  }
  return <img src={url} alt="preview" className="image-upload-preview" />;
}

/** L'ancien nom, et le cas de loin le plus courant : une image, et rien d'autre. */
export function ImageUpload(props) {
  return <MediaUpload {...props} accept="image" label={props.label ?? "Image"} />;
}

/* ─── IconUpload ─────────────────────────────────────────────────────
   Variante compacte de ImageUpload pour les petites icônes (scores,
   indicateurs…) affichées en ligne dans une liste. Même mécanique
   d'upload/drag-and-drop, gabarit réduit à une pastille carrée.
*/
export function IconUpload({ value, onChange, token, title = "Icône" }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const fileRef = useRef();

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      alert("Veuillez sélectionner un fichier image.");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("image", file);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload_image`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: form,
      });
      const data = await res.json();
      if (data.image_url) onChange(data.image_url);
      else alert("Erreur lors de l'upload.");
    } catch {
      alert("Erreur réseau lors de l'upload.");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  return (
    <div className="icon-upload-wrapper">
      <div
        className={`icon-upload-zone ${dragging ? "dragging" : ""} ${uploading ? "uploading" : ""}`}
        title={title}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && fileRef.current.click()}
      >
        {uploading ? (
          <span className="icon-upload-spinner">⏳</span>
        ) : value ? (
          <img src={value} alt="" className="icon-upload-preview" />
        ) : (
          <span className="icon-upload-placeholder">🖼</span>
        )}
        {value && !uploading && (
          <button
            type="button"
            className="icon-upload-clear-badge"
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            title="Retirer cette icône"
          >×</button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
        />
      </div>
      <button
        type="button"
        className="icon-upload-url-toggle"
        onClick={() => setShowUrl((v) => !v)}
        title="Saisir une URL manuellement"
      >🔗</button>
      {showUrl && (
        <input
          className="builder-input small"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL de l'icône"
        />
      )}
    </div>
  );
}

/* ─── TagInput ──────────────────────────────────────────────────────
   Tag chips editor with autocomplete suggestions.

   CE QUI EST TAPÉ COMPTE, MÊME SANS ENTRÉE. Le tag n'était ajouté que sur
   Entrée ou par clic dans la liste : taper « bpsud » puis cliquer droit sur
   « Enregistrer » laissait le texte dans le champ et la page partait avec
   `tags: []` — retour d'animateur, et des pages en base qui le confirment.
   La perte de focus et la virgule valident donc le texte en attente.
*/
export function TagInput({ tags = [], onChange, suggestions = [] }) {
  const [input, setInput] = useState("");
  const [showSugg, setShowSugg] = useState(false);
  const wrapperRef = useRef();

  const filtered = suggestions.filter(
    (s) => !tags.includes(s) && s.toLowerCase().includes(input.toLowerCase())
  );

  const addTag = (tag) => {
    const t = tag.trim().toLowerCase().replace(/\s+/g, "_");
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput("");
    setShowSugg(false);
  };

  const removeTag = (tag) => onChange(tags.filter((t) => t !== tag));

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSugg(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="tag-input-wrapper" ref={wrapperRef}>
      <div className="tag-input-chips">
        {tags.map((t) => (
          <span key={t} className="tag-chip">
            {t}
            <button
              className="tag-chip-remove"
              onClick={() => removeTag(t)}
              type="button"
            >×</button>
          </span>
        ))}
        <div className="tag-input-field-wrap">
          <input
            className="tag-input-field"
            value={input}
            onChange={(e) => { setInput(e.target.value); setShowSugg(true); }}
            onFocus={() => setShowSugg(true)}
            // Le clic sur une suggestion fait `preventDefault` sur mousedown :
            // le champ garde le focus, on n'ajoute pas deux fois.
            onBlur={() => { if (input.trim()) addTag(input); else setShowSugg(false); }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === ",") && input.trim()) { e.preventDefault(); addTag(input); }
              if (e.key === "Backspace" && !input && tags.length) removeTag(tags[tags.length - 1]);
            }}
            placeholder={tags.length === 0 ? "Ajouter un tag…" : ""}
          />
          {showSugg && (input || filtered.length > 0) && (
            <div className="tag-suggestions">
              {filtered.map((s) => (
                <div
                  key={s}
                  className="tag-suggestion-item"
                  onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                >
                  {s}
                </div>
              ))}
              {input.trim() && !tags.includes(input.trim()) && !suggestions.includes(input.trim()) && (
                <div
                  className="tag-suggestion-item new"
                  onMouseDown={(e) => { e.preventDefault(); addTag(input); }}
                >
                  + Créer « {input.trim()} »
                </div>
              )}
              {filtered.length === 0 && !input.trim() && (
                <div className="tag-suggestion-empty">Tapez pour créer un tag</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── RefSelect ─────────────────────────────────────────────────────
   Select recherchable pour référencer un objet existant par son ID
   (score, page, décision…) au lieu de taper l'ID à la main.
   options: [{ value, label, sublabel? }]
   Si `options` est vide (ex: pas de contexte d'atelier disponible),
   se rabat automatiquement sur un simple <input> texte libre.
*/
export function RefSelect({ value, onChange, options = [], placeholder = "Sélectionner…", allowClear = true, freeText = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Pas d'options connues (hors contexte d'atelier) : simple input texte, avec avertissement.
  if (freeText || options.length === 0) {
    return (
      <div className="ref-select-fallback">
        <input
          className="builder-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <span className="ref-select-fallback-hint">Saisie libre — aucun contexte d'atelier disponible pour proposer une liste.</span>
      </div>
    );
  }

  const selected = options.find((o) => o.value === value);
  const filtered = options.filter((o) =>
    o.value.toLowerCase().includes(search.toLowerCase()) ||
    (o.label || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="ref-select-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className={`ref-select-control ${!value ? "empty" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <span className="ref-select-value">
            <span className="ref-select-value-label">{selected.label || selected.value}</span>
            <span className="ref-select-value-id">{selected.value}</span>
          </span>
        ) : (
          <span className="ref-select-placeholder">{value || placeholder}</span>
        )}
        <span className="ref-select-arrow">▾</span>
      </button>
      {allowClear && value && (
        <button type="button" className="ref-select-clear" onClick={() => onChange("")} title="Effacer">✕</button>
      )}
      {open && (
        <div className="ref-select-dropdown">
          <input
            className="ref-select-search"
            autoFocus
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="ref-select-list">
            {filtered.slice(0, 100).map((o) => (
              <div
                key={o.value}
                className={`ref-select-item ${o.value === value ? "active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); setSearch(""); }}
              >
                <span className="ref-select-item-label">{o.label || o.value}</span>
                {o.sublabel && <span className="ref-select-item-sub">{o.sublabel}</span>}
              </div>
            ))}
            {filtered.length === 0 && <div className="tag-suggestion-empty">Aucun résultat</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── SortableList ──────────────────────────────────────────────────
   Liste réordonnable par drag & drop (poignée ⠿) avec fallback clavier
   (flèches ↑/↓ via les boutons fournis par renderItem). Remplace les
   listes qui ne pouvaient être réordonnées que d'une position à la fois.
   items: [{ id: string, ... }] — id doit être stable et unique.
   onReorder(newItemsArray)
   renderItem(item, index) doit rendre le contenu (hors poignée de drag).
*/
export function SortableList({ items, onReorder, renderItem, className = "" }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item, idx) => (
            <SortableItem key={item.id} id={item.id}>
              {renderItem(item, idx)}
            </SortableItem>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="sortable-item-wrapper">
      <button type="button" className="sortable-drag-handle" title="Glisser pour réordonner" {...attributes} {...listeners}>⠿</button>
      <div className="sortable-item-content">{children}</div>
    </div>
  );
}

/* ─── ConsequenceSummary ────────────────────────────────────────────
   Phrase lisible résumant une conséquence conditionnelle, pour rendre
   compréhensible en un coup d'œil une logique dispersée en plusieurs
   selects (ex: "Si la décision « Végétaliser » a été prise → ajoute
   la page « Bilan phase 1 »").
   consequence: { condition, consequence_id, previous_decision_id, condition_metadata }
   lookups: { pageLabel(id), decisionLabel(id), scoreLabel(id) } → string|undefined
*/
export function ConsequenceSummary({ consequence: c, lookups = {} }) {
  const pageLabel = (id) => (id ? (lookups.pageLabel?.(id) || id) : null);
  const decisionLabel = (id) => (id ? (lookups.decisionLabel?.(id) || id) : null);
  const scoreLabel = (id) => (id ? (lookups.scoreLabel?.(id) || id) : null);

  const meta = c.condition_metadata || {};
  let conditionText = "Toujours";
  if (c.condition) {
    if (c.previous_decision_id !== undefined && meta.type === undefined) {
      // Modèle "décision" simplifié (DecisionEditor) : condition = decision précédente directement
      conditionText = c.previous_decision_id
        ? `Si la décision « ${decisionLabel(c.previous_decision_id)} » a été prise`
        : "Si une décision précédente a été prise (à choisir)";
    } else if (meta.type === "past_decision") {
      conditionText = meta.previous_decision_id
        ? `Si la décision « ${decisionLabel(meta.previous_decision_id)} » a été prise`
        : "Si une décision précédente a été prise (à choisir)";
    } else if (meta.type === "score_below") {
      conditionText = `Si le score « ${scoreLabel(meta.score_id) || "?"} » est en dessous de ${meta.value ?? "?"}`;
    } else if (meta.type === "score_above") {
      conditionText = `Si le score « ${scoreLabel(meta.score_id) || "?"} » est au dessus de ${meta.value ?? "?"}`;
    }
  }

  const consequenceText = c.consequence_id
    ? `ajoute la page « ${pageLabel(c.consequence_id)} »`
    : "n'ajoute rien (page conséquence non définie)";

  const variationCount = c.variation_game_config ? Object.keys(c.variation_game_config).length : 0;

  return (
    <p className="consequence-summary">
      {conditionText} → {consequenceText}
      {variationCount > 0 && ` (+ ${variationCount} variante${variationCount > 1 ? "s" : ""} par atelier)`}
    </p>
  );
}

/* ─── ConsequenceCard ───────────────────────────────────────────────
   Enveloppe visuelle commune pour une ligne de conséquence (page ou
   décision) : badge "Toujours"/"Conditionnelle", résumé en phrase,
   alerte si la page conséquence référencée n'existe plus, et une zone
   pour les contrôles d'édition spécifiques (passés en children).
   consequence: { condition, consequence_id, previous_decision_id?, condition_metadata? }
   lookups: mêmes lookups que ConsequenceSummary, + pageExists(id) → bool
*/
export function ConsequenceCard({ consequence: c, lookups = {}, onRemove, children }) {
  const pageMissing = !!c.consequence_id && lookups.pageExists && !lookups.pageExists(c.consequence_id);

  return (
    <div className="consequence-card">
      <div className="consequence-card-header">
        <span className={`builder-tag ${c.condition ? "tag-conditional" : "tag-always"}`}>
          {c.condition ? "◆ Conditionnelle" : "● Toujours"}
        </span>
        <ConsequenceSummary consequence={c} lookups={lookups} />
        {onRemove && (
          <button className="builder-icon-btn danger consequence-card-remove" onClick={onRemove} title="Supprimer cette conséquence">✕</button>
        )}
      </div>
      {pageMissing && (
        <div className="builder-warning-inline">⚠ La page « {c.consequence_id} » référencée n'existe plus (ou plus).</div>
      )}
      <div className="consequence-card-body">{children}</div>
    </div>
  );
}

/* ─── InfoTooltip ───────────────────────────────────────────────────
   Petit "?" au survol affiche une explication du champ.
*/
export function InfoTooltip({ text }) {
  return (
    <span className="info-tooltip" title={text}>
      <span className="info-icon">?</span>
      <span className="info-tooltip-text">{text}</span>
    </span>
  );
}

/* ─── FieldLabel ────────────────────────────────────────────────────
   Label + tooltip combinés.
*/
export function FieldLabel({ children, tooltip }) {
  return (
    <label className="builder-label">
      <span className="field-label-row">
        <span>{children}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </span>
    </label>
  );
}

/* ─── SideDrawer ────────────────────────────────────────────────────
   Panneau latéral glissant depuis la droite.
   stack: [{ label: string }] — pile d'éléments ouverts (breadcrumb).
   onClose: ferme tout.
   onPop: retire le dernier élément (revenir en arrière dans la pile).
*/
export function SideDrawer({ stack, onClose, onPop, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (stack.length > 1) onPop();
        else onClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [stack, onClose, onPop]);

  return (
    <>
      <div className="side-drawer-backdrop" onClick={onClose} />
      <div className="side-drawer">
        <div className="side-drawer-header">
          <div className="side-drawer-breadcrumb">
            {stack.length > 1 && (
              <button className="side-drawer-back" onClick={onPop} title="Retour">
                ←
              </button>
            )}
            {stack.map((item, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="side-drawer-sep">›</span>}
                <span className={`side-drawer-crumb${i === stack.length - 1 ? " active" : ""}`}>
                  {item.label}
                </span>
              </React.Fragment>
            ))}
          </div>
          <button className="side-drawer-close" onClick={onClose} title="Fermer (Échap)">✕</button>
        </div>
        <div className="side-drawer-content">
          {children}
        </div>
      </div>
    </>
  );
}

/**
 * OÙ REVIENT-ON EN QUITTANT UN ÉDITEUR.
 *
 * Le bouton « ← Retour » ne disait pas où il ramenait, et selon l'écran il
 * faisait `navigate(-1)` (donc « la page d'avant », qui pouvait être
 * n'importe quoi après quelques allers-retours) ou un retour codé en dur vers
 * la bibliothèque. Résultat : on éditait une page depuis un atelier et on
 * atterrissait ailleurs.
 *
 * L'origine voyage donc désormais avec la navigation, dans `location.state`,
 * sous la forme `{ path, label }` — et le bouton AFFICHE ce libellé. Un
 * bouton qui nomme sa destination est le moyen le plus simple de rendre une
 * navigation prévisible.
 *
 * `returnTab` reste accepté : c'est l'ancienne convention de la bibliothèque,
 * encore posée par WorkshopBuilder.
 */
export function returnTarget(location) {
  const from = location?.state?.from;
  if (from?.path) return { path: from.path, label: from.label || "Retour" };
  const tab = location?.state?.returnTab;
  return { path: `/builder${tab ? `?tab=${tab}` : ""}`, label: "Bibliothèque" };
}

/* ─── Identifiants de décisions d'une page ────────────────────────────
   `/get_page` renvoie `list_decisions` en IDs bruts, `/get_complete_page` en
   documents résolus. Les deux formes circulent dans le builder ; tout ce qui
   enregistre a besoin de la première.
*/
export function decisionIdsOf(page) {
  return (page?.content?.list_decisions || [])
    .map((d) => (typeof d === "string" ? d : d?._id))
    .filter(Boolean);
}

/**
 * ENREGISTRER UNE PAGE : UNE SEULE RÈGLE, UN SEUL ENDROIT.
 *
 * `/save_page` ne répond pas seulement « c'est écrit ». Il peut REFUSER (409 :
 * une page localisée employée par un atelier d'un autre territoire) ou BLOQUER
 * (`blocked` : la modification casse le parcours d'au moins un atelier), auquel
 * cas l'auteur peut forcer en connaissance de cause.
 *
 * Ces trois issues n'étaient traitées qu'à un seul endroit — le formulaire.
 * L'aperçu, lui, jetait la réponse : la modification n'était pas écrite, le
 * rechargement qui suivait la faisait disparaître de l'écran, et rien ne disait
 * pourquoi. Un refus silencieux est pire qu'une erreur : l'auteur croit avoir
 * enregistré. Tout chemin d'enregistrement passe donc désormais par ici.
 *
 * Ne montre RIEN de lui-même sauf les confirmations (qui sont des questions, pas
 * des comptes rendus) : l'appelant décide comment annoncer l'échec.
 * Rend { ok } · { ok: false, cancelled: true } · { ok: false, message }.
 */
export async function savePageWithGuards(payload, {
  token, isNew = false, existingIds = null, usedByConfigs = null, confirmUsage = true,
} = {}) {
  const id = (payload._id || "").trim();
  if (!id) return { ok: false, message: "L'identifiant de la page est requis." };
  if (!(payload.title || "").trim()) return { ok: false, message: "Le titre est requis." };

  if (isNew) {
    if (!isValidSlug(id)) return { ok: false, message: `Identifiant invalide.\n${SLUG_HINT}` };
    if (existingIds && existingIds.includes(id)) {
      return { ok: false, message: `L'identifiant « ${id} » est déjà utilisé par une autre page.` };
    }
  }

  // Une page est partagée : la modifier modifie chaque atelier qui l'emploie.
  if (confirmUsage && usedByConfigs && usedByConfigs.length > 0) {
    const proceed = window.confirm(
      `Cette page est utilisée par ${usedByConfigs.length} atelier(s) : ${usedByConfigs.join(", ")}.\n` +
      `Cette modification s'appliquera à tous ces ateliers. Voulez-vous continuer ?\n\n` +
      `Astuce : utilisez « Dupliquer » sur l'élément concerné si vous voulez une variante propre à un seul atelier.`
    );
    if (!proceed) return { ok: false, cancelled: true };
  }

  const post = async (body) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/save_page`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    // 409 et 500 répondent du JSON eux aussi : c'est le corps qui porte
    // l'explication, pas le code. On ne lit donc pas `res.ok`.
    try {
      return await res.json();
    } catch {
      return { success: false, message: `Réponse illisible du serveur (HTTP ${res.status}).` };
    }
  };

  let res = await post(payload);
  if (res.success) return { ok: true, response: res };

  if (res.blocked) {
    const details = Object.entries(res.errors_by_config || {})
      .map(([cfgId, findings]) => `« ${cfgId} » :\n${findings.map((f) => `  - ${f.message}`).join("\n")}`)
      .join("\n\n");
    const forceOk = window.confirm(
      `Enregistrer cette page va provoquer un plantage pour au moins un joueur dans ` +
      `${Object.keys(res.errors_by_config || {}).length} atelier(s) :\n\n${details}\n\n` +
      `Forcer l'enregistrement quand même ?`
    );
    if (!forceOk) return { ok: false, cancelled: true };
    res = await post({ ...payload, _force_save: true });
    if (res.success) return { ok: true, response: res, forced: true };
  }

  return { ok: false, message: res.message || "Enregistrement refusé par le serveur." };
}

/**
 * ENREGISTRER UNE DÉCISION : même règle, même forme que `savePageWithGuards`.
 *
 * Elle a sa propre route et ses propres vérifications côté serveur, mais les
 * trois issues sont les mêmes — écrit, refusé, bloqué-mais-forçable — et une
 * décision est partagée exactement comme une page. Les tableaux de synthèse du
 * composeur modifient des décisions ; ils passent par ici, comme l'éditeur de
 * décision lui-même.
 *
 * `"" ` veut dire « aucune page choisie » côté formulaire ; on normalise en
 * `null` avant l'envoi, parce que le moteur teste `is not None` et traiterait
 * une chaîne vide comme une page à ajouter.
 */
export async function saveDecisionWithGuards(decision, {
  token, isNew = false, existingIds = null, usedByConfigs = null, confirmUsage = true,
} = {}) {
  const id = (decision._id || "").trim();
  if (!id) return { ok: false, message: "L'identifiant de la décision est requis." };
  if (!(decision.title || "").trim()) return { ok: false, message: "Le titre est requis." };

  if (isNew) {
    if (!isValidSlug(id)) return { ok: false, message: `Identifiant invalide.\n${SLUG_HINT}` };
    if (existingIds && existingIds.includes(id)) {
      return { ok: false, message: `L'identifiant « ${id} » est déjà utilisé par une autre décision.` };
    }
  }

  if (confirmUsage && usedByConfigs && usedByConfigs.length > 0) {
    const proceed = window.confirm(
      `Cette décision est utilisée par ${usedByConfigs.length} atelier(s) : ${usedByConfigs.join(", ")}.\n` +
      `Cette modification s'appliquera à tous ces ateliers. Voulez-vous continuer ?\n\n` +
      `Astuce : dupliquez cette décision (depuis la liste de décisions d'une page) si vous voulez une variante propre à un seul atelier.`
    );
    if (!proceed) return { ok: false, cancelled: true };
  }

  const normalize = (list) => (list || []).map((c) => ({ ...c, consequence_id: c.consequence_id || null }));
  const payload = {
    ...decision,
    add_elem_to_pile_if_taken: normalize(decision.add_elem_to_pile_if_taken),
    add_elem_to_pile_if_not_taken: normalize(decision.add_elem_to_pile_if_not_taken),
  };

  const post = async (body) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/save_decision`, {
      method: "POST",
      headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    try {
      return await res.json();
    } catch {
      return { success: false, message: `Réponse illisible du serveur (HTTP ${res.status}).` };
    }
  };

  let res = await post(payload);
  if (res.success) return { ok: true, response: res };

  if (res.blocked) {
    const details = Object.entries(res.errors_by_config || {})
      .map(([cfgId, findings]) => `« ${cfgId} » :\n${findings.map((f) => `  - ${f.message}`).join("\n")}`)
      .join("\n\n");
    const forceOk = window.confirm(
      `Enregistrer « ${id} » va provoquer un plantage pour au moins un joueur dans ` +
      `${Object.keys(res.errors_by_config || {}).length} atelier(s) :\n\n${details}\n\n` +
      `Forcer l'enregistrement quand même ?`
    );
    if (!forceOk) return { ok: false, cancelled: true };
    res = await post({ ...payload, _force_save: true });
    if (res.success) return { ok: true, response: res, forced: true };
  }

  return { ok: false, message: res.message || "Enregistrement refusé par le serveur." };
}

/**
 * game_status factice, construit à partir des valeurs INITIALES du score_dic de
 * la config. Il permet aux composants de jeu (jauges, doughnuts, radars) de
 * s'afficher dans le builder sans dépendre d'une partie réelle — aperçu de
 * page comme canevas d'étape.
 *
 * Ce qu'il montre est donc un début de partie, pas un milieu : les jauges y
 * sont à plat. C'est une limite assumée de tout aperçu hors jeu.
 */
export function buildMockGameStatus(scoreDic, decisionMetricId, config = null) {
  return {
    score_dic: scoreDic || {},
    decision_metric_id: decisionMetricId,
    // actor_pannel_cg / actor_pannel lisent list_selected_actors (normalement
    // rempli en jeu réel selon les décisions prises) : on simule avec tous les
    // scores de type "actor" de la config pour permettre l'aperçu.
    list_selected_actors: Object.entries(scoreDic || {})
      .filter(([, s]) => s.type === "actor")
      .map(([key]) => key),
    // Les écrans de bilan lisent le nombre de tours pour situer l'objectif.
    nb_rounds_cg: config?.nb_rounds_cg,
    target_per_round: config?.target_per_round,
    // Aucun tour validé : la courbe d'objectif de la projection d'eau ne se
    // montre pas dans l'aperçu, comme sur une partie qui commence.
    current_round: 0,
    // JOURNAL DES MESURES VOLONTAIREMENT ABSENT. Le bilan ACG s'en sert pour
    // les titres, les concessions et le clivage : le simuler donnerait un
    // aperçu qui montre plus que ce qu'un début de partie contient, donc une
    // mise en page fausse. Les blocs concernés s'effacent, comme ils le feront
    // sur une partie commencée avant le journal — c'est le cas qu'un auteur a
    // besoin de voir.
    //
    // Pas de `_id` non plus : le classement parmi les autres séances n'a rien à
    // interroger dans le builder, et son bloc ne s'affiche pas.
  };
}
